/**
 * SpotifySearchPanel — inline expandable Spotify search.
 *
 * Renders the search input on submit + a max-height results list with the
 * first 6 tracks and first 6 playlists from /api/spotify/search.
 *
 * Visibility is parent-controlled (`searchOpen`) — this component just renders
 * the form and the results list conditionally based on the same flag.
 */
import React from "react";
import { Search, X, Loader2 } from "lucide-react";
import SpotifyTrackRow from "./SpotifyTrackRow";

export default function SpotifySearchPanel({
  searchOpen,
  query, setQuery,
  searchResults,
  searching,
  runSearch,
  onClose,
  onPlay,
}) {
  if (!searchOpen) return null;

  const showResults = searching || searchResults;

  return (
    <>
      <form
        onSubmit={runSearch}
        className="flex items-center gap-2 mb-3 rounded-full bg-white/5 border border-white/10 backdrop-blur-xl px-4 py-1.5"
        data-testid="spotify-search-form"
      >
        <Search className="w-4 h-4 text-white/40" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tracks and playlists…"
          autoFocus
          className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-white/30"
          data-testid="spotify-search-input"
        />
        <button type="submit" className="text-xs uppercase tracking-widest text-[#6B9BFF] hover:text-white">
          Go
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-white/40 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </form>

      {showResults && (
        <div
          className="max-h-72 overflow-y-auto rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl mb-3"
          data-testid="spotify-search-results"
        >
          {searching ? (
            <div className="p-4 text-white/40 text-sm inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
            </div>
          ) : (
            <>
              {(searchResults?.tracks || []).slice(0, 6).map((t, i) => (
                <SpotifyTrackRow
                  key={`s-t-${t.id}`}
                  item={t}
                  kind="tracks"
                  onPlay={onPlay}
                  testid={`spotify-search-track-${i}`}
                />
              ))}
              {(searchResults?.playlists || []).slice(0, 6).map((p, i) => (
                <SpotifyTrackRow
                  key={`s-p-${p.id}`}
                  item={p}
                  kind="playlists"
                  onPlay={onPlay}
                  testid={`spotify-search-playlist-${i}`}
                />
              ))}
              {!(searchResults?.tracks?.length || searchResults?.playlists?.length) && (
                <div className="p-4 text-white/40 text-sm">No results.</div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
